import { format } from 'date-fns/format';
import { Dispatch } from 'react';

import {
  Actions as campaignListActions,
  CampaignListReducerAction,
  CampaignListReducerState,
} from '@features/campaigns/list/types/campaignListReducer.ts';
import { CampaignListFilterDefinitionKey } from '@features/campaigns/list/utils/campaignListFiltersStorage.ts';
import {
  CampaignCountryType,
  CampaignFormat,
  CampaignStatus,
  CampaignType,
} from '@features/campaigns/shared/types/campaign.ts';
import { Nullable } from '@shared/types/common';

interface CampaignListFilterDefinition {
  key: CampaignListFilterDefinitionKey;
  serializeFromState: (state: CampaignListReducerState) => string | undefined;
  hydrate: (value: string, dispatch: Dispatch<CampaignListReducerAction>) => void;
}

const formatFilterDate = (date: Date): string => format(date, 'yyyy-MM-dd');

const createSearchFilterDefinition = (): CampaignListFilterDefinition => ({
  key: 'search',
  serializeFromState: (state) => (state.search && state.search.length > 0 ? state.search : undefined),
  hydrate: (value, dispatch) => {
    dispatch({
      type: campaignListActions.CHANGE_SEARCH_QUERY,
      payload: { search: value },
    });
  },
});

const createArrayFilterDefinition = <TArrayValue extends string>({
  key,
  getArrayValue,
  hydrateAction,
  mapHydratedValue,
}: {
  key: Exclude<CampaignListFilterDefinitionKey, 'search' | 'startDate' | 'endDate'>;
  getArrayValue: (state: CampaignListReducerState) => TArrayValue[] | undefined;
  hydrateAction:
    | typeof campaignListActions.CHANGE_STATUS_FILTER
    | typeof campaignListActions.CHANGE_TYPE_FILTER
    | typeof campaignListActions.CHANGE_COUNTRY_FILTER
    | typeof campaignListActions.CHANGE_FORMAT_FILTER
    | typeof campaignListActions.CHANGE_ADVERTISER_FILTER;
  mapHydratedValue: (values: TArrayValue[]) => CampaignListReducerAction['payload'];
}): CampaignListFilterDefinition => ({
  key,
  serializeFromState: (state) => {
    const arrayValue = getArrayValue(state);

    return arrayValue && arrayValue.length > 0 ? arrayValue.join(',') : undefined;
  },
  hydrate: (value, dispatch) => {
    const splittedFilterValues = value.split(',') as TArrayValue[];

    dispatch({
      type: hydrateAction,
      payload: mapHydratedValue(splittedFilterValues),
    });
  },
});

const createDateFilterDefinition = ({
  key,
  getDateValue,
  hydrateAction,
}: {
  key: 'startDate' | 'endDate';
  getDateValue: (state: CampaignListReducerState) => Nullable<Date> | undefined;
  hydrateAction:
    | typeof campaignListActions.CHANGE_START_DATE_FILTER
    | typeof campaignListActions.CHANGE_END_DATE_FILTER;
}): CampaignListFilterDefinition => ({
  key,
  serializeFromState: (state) => {
    const dateValue = getDateValue(state);

    return dateValue ? formatFilterDate(new Date(`${dateValue}`)) : undefined;
  },
  hydrate: (value, dispatch) => {
    dispatch({
      type: hydrateAction,
      payload: { filters: { [key]: new Date(value) } },
    });
  },
});

export const campaignListFilterDefinitions: Record<CampaignListFilterDefinitionKey, CampaignListFilterDefinition> = {
  advertiser: createArrayFilterDefinition({
    key: 'advertiser',
    getArrayValue: (state) => state.filters?.advertiser,
    hydrateAction: campaignListActions.CHANGE_ADVERTISER_FILTER,
    mapHydratedValue: (values) => ({ filters: { advertiser: values } }),
  }),
  search: createSearchFilterDefinition(),
  status: createArrayFilterDefinition({
    key: 'status',
    getArrayValue: (state) => state.filters?.status,
    hydrateAction: campaignListActions.CHANGE_STATUS_FILTER,
    mapHydratedValue: (values) => ({ filters: { status: values as CampaignStatus[] } }),
  }),
  type: createArrayFilterDefinition({
    key: 'type',
    getArrayValue: (state) => state.filters?.type,
    hydrateAction: campaignListActions.CHANGE_TYPE_FILTER,
    mapHydratedValue: (values) => ({ filters: { type: values as CampaignType[] } }),
  }),
  country: createArrayFilterDefinition({
    key: 'country',
    getArrayValue: (state) => state.filters?.country,
    hydrateAction: campaignListActions.CHANGE_COUNTRY_FILTER,
    mapHydratedValue: (values) => ({ filters: { country: values as CampaignCountryType[] } }),
  }),
  format: createArrayFilterDefinition({
    key: 'format',
    getArrayValue: (state) => state.filters?.format,
    hydrateAction: campaignListActions.CHANGE_FORMAT_FILTER,
    mapHydratedValue: (values) => ({ filters: { format: values as CampaignFormat[] } }),
  }),
  startDate: createDateFilterDefinition({
    key: 'startDate',
    getDateValue: (state) => state.filters?.startDate,
    hydrateAction: campaignListActions.CHANGE_START_DATE_FILTER,
  }),
  endDate: createDateFilterDefinition({
    key: 'endDate',
    getDateValue: (state) => state.filters?.endDate,
    hydrateAction: campaignListActions.CHANGE_END_DATE_FILTER,
  }),
};

export const campaignListFilterDefinitionList = Object.values(campaignListFilterDefinitions);
